/**
 * Canonical UHID derivation — single source of truth shared across every
 * print surface (printToken.js, printQueueSheet.js). There must never be a
 * second copy of any of these functions — see the "single source of truth"
 * describe block in `uhid.test.js`.
 *
 * T-UHID-BUG (owner live-test finding, 2026-08-26) — a printed slip showed
 * `A3B75` (a sliced fragment of the internal `patient_id`) labelled "UHID"
 * for a patient whose real UHID is `UHID/000148`. Ruling: MONEY documents
 * (receipt/token/credit slip) print the FULL real UHID — `resolveUhid()` —
 * when one was minted.
 *
 * OWNER REVERSAL (2026-08-26, same day) — `resolveUhid`'s NO-uhid fallback
 * stays the short last-5 form, NOT the full patient_id. UHID minting shipped
 * 2026-08-03 (`83e151f`); there is no backfill script anywhere in the repo,
 * so EVERY patient registered before that date has no `uhid` PERMANENTLY —
 * for a pilot hospital this can be the whole existing patient base, not an
 * edge case. `patient_id` is a full UUID4 (`_generate_id()`,
 * patient_repository.py) — rendering it in full wraps the 80mm thermal
 * token/slip to two lines, which is worse than the original bug. So the
 * no-uhid branch is DELIBERATELY still `deriveUhid(patient_id)`, byte-
 * identical to the shipped 2026-08-13 behaviour (`23cf6e5`) — do not
 * "fix" this back to a full fallback without re-litigating this ruling.
 * Lazy-minting a UHID for a pre-2026-08-03 patient on their next visit is
 * tracked separately as bk-737 (priced plan, out of scope here).
 *
 * D36 (owner, 2026-08-26, same day) — REVERSES half of D34. The printed
 * patient list (`printQueueSheet.js`) also carries money (Net Paid,
 * Receipt Id, on BOTH its OPD and Lab columns), so it now prints the FULL
 * UHID too — the SAME `resolveUhid()` money-document helper, not a
 * separate short-form one. The dedicated `resolveShortUhid` helper this
 * sheet used to call is gone (no other caller — dead code removed per
 * standing rule); its short-form behaviour survives unchanged as
 * `resolveUhid`'s own no-uhid fallback above. A pre-2026-08-03 patient
 * (D35) still legitimately prints the short fallback on this sheet — the
 * column will honestly mix `UHID/000148` with a bare `A3B75`; that is
 * intended, not a bug.
 */

/**
 * deriveUhid — shorten any id-like string to its last 5 characters,
 * uppercased (whole string uppercased when shorter). `resolveUhid` uses
 * this ONLY on its no-uhid fallback (see the OWNER REVERSAL note above).
 */
export function deriveUhid(idish) {
  if (!idish) return '';
  const id = String(idish);
  return id.length <= 5 ? id.toUpperCase() : id.slice(-5).toUpperCase();
}

/**
 * resolveUhid — every UHID-bearing money document: the OPD/procedure
 * token + its reprint, the IPD lab-credit slip, and (D36) the printed
 * patient list / queue sheet (both OPD and Lab columns). The FULL real
 * UHID (`patient.uhid` / `VisitResponse.patient_uhid`, e.g. `UHID/000148`)
 * when the backend minted one; else the last-5 `deriveUhid(patient_id)`
 * fallback, byte-identical to the pre-T-UHID-BUG shipped behaviour (owner
 * reversal, see file header — a full patient_id UUID wraps the thermal
 * slip and pre-mint patients are the common case, not rare).
 */
export function resolveUhid({ uhid, patient_id } = {}) {
  return uhid || deriveUhid(patient_id);
}
